/**
 * home/sections/TransparencySection.tsx
 * Transparencia y atribución — DS v2 (H.06)
 *
 * @version 2.3.1
 *   - Barras de cobertura con color semántico por rango (ámbar/teal/lima)
 *   - Iconos de tarjeta ampliados, sin recuadro (transp-card-ico--bare)
 *   - Textos de tarjeta reequilibrados a densidad homogénea
 *   - Tooltips CSS en pie normativo (Decreto, Junta, INSPIRE, IECA, EPSG)
 *   - backgroundColor inline: evita conflictos de especificidad post-minificación
 *   - display:block + height:100% inline: <span> es inline por defecto, sin esto no renderiza
 */
import type { FC } from 'react';

type EstadoType = 'online' | 'degraded' | 'offline';

interface DataSource {
  dataset: string;
  provider: string;
  licClass: 'lic-cc' | 'lic-inspire' | 'lic-odbl';
  licLabel: string;
  licenseUrl: string;
  records: string;
  url: string;
  estado: EstadoType;
  estadoLabel: string;
  cobertura?: number;
}

const DATA_SOURCES: DataSource[] = [
  { dataset: 'CDAU',        provider: 'Junta de Andalucía',        licClass: 'lic-cc',      licLabel: 'CC BY 4.0', licenseUrl: 'https://creativecommons.org/licenses/by/4.0/deed.es', records: '3,2 M portales',      url: 'https://www.callejerodeandalucia.es/',                              estado: 'online', estadoLabel: 'En línea', cobertura: 97  },
  { dataset: 'Catastro',    provider: 'DG del Catastro',            licClass: 'lic-inspire', licLabel: 'Licencia del Catastro', licenseUrl: 'https://www.catastro.hacienda.gob.es/webinspire/documentos/Licencia.pdf', records: '2,4 M edificaciones', url: 'https://www.catastro.meh.es/',                                      estado: 'online', estadoLabel: 'En línea', cobertura: 100 },
  { dataset: 'NGA',         provider: 'IGN España',                 licClass: 'lic-cc',      licLabel: 'CC BY 4.0', licenseUrl: 'https://creativecommons.org/licenses/by/4.0/deed.es', records: '1,8 M topónimos',     url: 'https://centrodedescargas.cnig.es/',                                estado: 'online', estadoLabel: 'En línea', cobertura: 100 },
  { dataset: 'DERA',        provider: 'IECA — Junta de Andalucía', licClass: 'lic-cc',      licLabel: 'CC BY 4.0', licenseUrl: 'https://creativecommons.org/licenses/by/4.0/deed.es', records: '45 800+ features',    url: 'https://www.juntadeandalucia.es/institutodeestadisticaycartografia', estado: 'online', estadoLabel: 'En línea', cobertura: 100 },
  { dataset: 'IAPH',        provider: 'Junta de Andalucía',         licClass: 'lic-cc',      licLabel: 'CC BY 4.0', licenseUrl: 'https://creativecommons.org/licenses/by/4.0/deed.es', records: '18 900 bienes',       url: 'https://www.iaph.es/',                                              estado: 'online', estadoLabel: 'En línea', cobertura: 85  },
  { dataset: 'AGOL PTEL',   provider: 'Emergencias 112',            licClass: 'lic-cc',      licLabel: 'CC BY 4.0', licenseUrl: 'https://creativecommons.org/licenses/by/4.0/deed.es', records: '36 000+ elementos',   url: 'https://www.arcgis.com/',                                           estado: 'online', estadoLabel: 'En línea', cobertura: 100 },
  { dataset: 'CartoCiudad', provider: 'IGN España',                 licClass: 'lic-cc',      licLabel: 'CC BY 4.0', licenseUrl: 'https://creativecommons.org/licenses/by/4.0/deed.es', records: 'API en línea',        url: 'https://www.cartociudad.es/',                                       estado: 'online', estadoLabel: 'En línea', cobertura: 95  },
  { dataset: 'Edu Centers', provider: 'Consejería Educación',       licClass: 'lic-cc',      licLabel: 'CC BY 4.0', licenseUrl: 'https://creativecommons.org/licenses/by/4.0/deed.es', records: '7 100 centros',       url: 'https://www.juntadeandalucia.es/educacion/',                        estado: 'online', estadoLabel: 'En línea', cobertura: 90  },
  { dataset: 'OSM',         provider: 'OpenStreetMap Contributors', licClass: 'lic-odbl',    licLabel: 'ODbL 1.0',  licenseUrl: 'https://opendatacommons.org/licenses/odbl/1-0/',        records: '791 instalaciones',   url: 'https://www.openstreetmap.org/',                                    estado: 'online', estadoLabel: 'En línea', cobertura: 100 },
];

const ESTADO_DOT_CLASS: Record<EstadoType, string> = {
  online:   'transp-estado-dot transp-estado-dot--online',
  degraded: 'transp-estado-dot transp-estado-dot--degraded',
  offline:  'transp-estado-dot transp-estado-dot--offline',
};

/**
 * Color semántico de barra según rango de cobertura.
 * Aplicado como backgroundColor inline para evitar conflictos de especificidad
 * CSS post-minificación (background shorthand puede ser ignorado por Vite/React).
 *   100%    → lima pura    #a3e635
 *   95–99%  → lima suave   rgba(163,230,53,0.65)
 *   85–94%  → teal         #2dd4bf
 *   <85%    → ámbar        #f59e0b
 */
const COB_COLOR: Array<[threshold: number, color: string]> = [
  [100, '#a3e635'],
  [95,  'rgba(163,230,53,0.65)'],
  [85,  '#2dd4bf'],
  [0,   '#f59e0b'],
];
function cobColor(pct: number): string {
  return (COB_COLOR.find(([t]) => pct >= t) ?? COB_COLOR[COB_COLOR.length - 1])[1];
}

export const TransparencySection: FC = () => (
  <section className="transp-section" id="transparency">
    <div className="transp-inner">

      <div className="transp-head">
        <div className="transp-eyebrow">Transparencia y atribución</div>
        <h2 className="transp-title">Fuentes de datos y licencias</h2>
        <p className="transp-subtitle">
          Cada fuente tiene su licencia (CC BY 4.0 en la mayoría, propia en el Catastro, ODbL en OpenStreetMap); se indica en cada fila.
        </p>
      </div>

      {/* ── Tabla de datasets ── */}
      <div className="transp-table-wrap">
        <table className="transp-table">
          <thead>
            <tr>
              <th>Dataset</th>
              <th>Proveedor</th>
              <th>Licencia</th>
              <th>Registros</th>
              <th>Estado</th>
              <th>Cobertura And.</th>
            </tr>
          </thead>
          <tbody>
            {DATA_SOURCES.map((s) => (
              <tr key={s.dataset}>
                <td><a href={s.url} target="_blank" rel="noopener noreferrer">{s.dataset}</a></td>
                <td>{s.provider}</td>
                <td>
                  <a href={s.licenseUrl} target="_blank" rel="noopener noreferrer"
                    className={`transp-badge-lic ${s.licClass}`}>
                    {s.licLabel}
                  </a>
                </td>
                <td className="transp-count">{s.records}</td>
                <td>
                  <span className="transp-estado-cell">
                    <span className={ESTADO_DOT_CLASS[s.estado]} aria-hidden="true" />
                    <span className="transp-estado-label">{s.estadoLabel}</span>
                  </span>
                </td>
                <td>
                  {s.cobertura !== undefined ? (
                    <span className="transp-cob-cell">
                      <span className="transp-cob-bar-bg" aria-hidden="true">
                        <span
                          className="transp-cob-bar-fill"
                          style={{ display: 'block', width: `${s.cobertura}%`, height: '100%', backgroundColor: cobColor(s.cobertura) }}
                        />
                      </span>
                      <span className="transp-cob-pct">{s.cobertura}%</span>
                    </span>
                  ) : (
                    <span className="transp-count">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="transp-table-note">
        Estado en tiempo real · Cobertura sobre 785 municipios andaluces
      </p>

      {/* ── Tarjetas de contexto ── */}
      <div className="transp-cards">

        {/* Tarjeta 1 — Privacidad */}
        <div className="transp-card">
          <div className="transp-card-ico transp-card-ico--bare">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div className="transp-card-title">Privacidad — qué sale de tu navegador</div>
          <p className="transp-card-text">
            El documento PTEL no se sube a ningún servidor: se lee en tu navegador. Para localizar cada infraestructura se consultan servicios cartográficos públicos (IGN, Junta de Andalucía, OpenStreetMap) con su nombre o su dirección. Las coordenadas que confirmes se guardan, con el nombre de la infraestructura y tu cuenta, en servidores situados en la Unión Europea.
          </p>
        </div>

        {/* Tarjeta 2 — Trazabilidad */}
        <div className="transp-card">
          <div className="transp-card-ico transp-card-ico--bare">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div className="transp-card-title">Cada coordenada indica de dónde viene</div>
          <p className="transp-card-text">
            El fichero exportado lleva, para cada coordenada, su procedencia, las fuentes con que se ha contrastado y su nivel de confianza.
          </p>
        </div>

        {/* Tarjeta 3 — Marco normativo · BookOpen */}
        <div className="transp-card">
          <div className="transp-card-ico transp-card-ico--bare">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
          </div>
          <div className="transp-card-title">Marco normativo</div>
          <p className="transp-card-text">
            Salida en ETRS89 · UTM huso 30 (EPSG:25830), el sistema geodésico
            de referencia oficial en España (Real Decreto 1071/2007).
          </p>
        </div>

      </div>

      {/* ── Pie normativo con tooltips ── */}
      <div className="transp-decree">
        <span className="transp-decree-ref">
          <strong>Decreto 69/2024</strong>
          <span className="transp-decree-tooltip">
            Aprueba el Plan Territorial de Emergencias de Protección Civil de Andalucía (PTEAnd),
            cuyo Anexo 1 contiene las directrices para elaborar los PTEL.
          </span>
        </span>
        {' · '}
        <span className="transp-decree-ref">
          Junta de Andalucía
          <span className="transp-decree-tooltip">
            Administración autonómica que promulga el Decreto 69/2024.
            Los datos utilizados provienen de sus organismos (IECA, Emergencias 112, Consejería de Educación).
          </span>
        </span>
        {' · '}
        <span className="transp-decree-ref">
          Normativa INSPIRE
          <span className="transp-decree-tooltip">
            Directiva europea (2007/2/CE) que establece estándares de interoperabilidad
            para compartir datos geoespaciales entre administraciones de la UE.
            España la incorporó mediante la Ley 14/2010.
          </span>
        </span>
        {' · '}
        <span className="transp-decree-ref">
          Estándares IECA
          <span className="transp-decree-tooltip">
            Instituto de Estadística y Cartografía de Andalucía: organismo de la Junta
            que publica los datos de referencia que usa PTEL Tool (DERA, CDAU y NGA).
          </span>
        </span>
        {' · '}
        <span className="transp-decree-ref">
          <strong>EPSG:25830</strong>
          <span className="transp-decree-tooltip">
            Sistema de coordenadas de salida: proyección UTM, huso 30 Norte, sobre ETRS89,
            el sistema geodésico de referencia oficial en España (Real Decreto 1071/2007).
            El «idioma» en que PTEL Tool entrega las coordenadas para superponerlas
            sobre la cartografía oficial.
          </span>
        </span>
        {' — UTM huso 30 ETRS89'}
      </div>

    </div>
  </section>
);

export default TransparencySection;
